import React from 'react';
import { motion, useReducedMotion, type Variants } from 'framer-motion';
import { DURATION, EASE_OUT, TRAVEL, VIEWPORT } from './variants';

type Direction = 'up' | 'down' | 'left' | 'right' | 'scale' | 'none';
type Tag = 'div' | 'section' | 'article' | 'header' | 'span' | 'p' | 'form';

/** Standard DOM props (incl. className, onSubmit, aria-*) minus the
    handful framer-motion redefines with different signatures. */
type DOMProps = Omit<
  React.HTMLAttributes<HTMLElement>,
  'onAnimationStart' | 'onAnimationEnd' | 'onAnimationIteration' | 'onDrag' | 'onDragStart' | 'onDragEnd'
>;

type Props = {
  children: React.ReactNode;
  /** Delay before the reveal starts (seconds). */
  delay?: number;
  /** The side the element animates in from. */
  direction?: Direction;
  /** Travel distance in px (ignored for `scale`). */
  distance?: number;
  /** Fraction of the element visible before triggering. */
  amount?: number;
  as?: Tag;
} & DOMProps;

const MOTION_TAGS = {
  div: motion.div,
  section: motion.section,
  article: motion.article,
  header: motion.header,
  span: motion.span,
  p: motion.p,
  form: motion.form,
};

const offset = (direction: Direction, distance: number): Record<string, number> => {
  switch (direction) {
    case 'up':
      return { y: distance };
    case 'down':
      return { y: -distance };
    case 'left':
      return { x: -distance };
    case 'right':
      return { x: distance };
    case 'scale':
      return { scale: 0.92 };
    default:
      return {};
  }
};

/**
 * Fade + slide reveal for non-text blocks (images, forms, cards,
 * paragraphs). Fires once when scrolled into view. Falls back to a
 * plain element when the user prefers reduced motion.
 */
const Reveal: React.FC<Props> = ({
  children,
  delay = 0,
  direction = 'up',
  distance = TRAVEL,
  amount,
  as = 'div',
  ...rest
}) => {
  const prefersReducedMotion = useReducedMotion();

  if (prefersReducedMotion) {
    const Plain = as as React.ElementType;
    return <Plain {...rest}>{children}</Plain>;
  }

  const variants: Variants = {
    hidden: { opacity: 0, ...offset(direction, distance) },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      transition: { duration: DURATION.section, ease: EASE_OUT, delay },
    },
  };

  const MotionTag = MOTION_TAGS[as] as React.ElementType;

  return (
    <MotionTag
      {...rest}
      variants={variants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: amount ?? VIEWPORT.amount }}
    >
      {children}
    </MotionTag>
  );
};

export default Reveal;
